import NoData from '@/components/common/NoData'
import SearchInput from '@/components/common/SearchInput'
import ButtonField from '@/components/form-control/ButtonField'
import CancelIcon from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { flyInVariantsDashboard } from '@/constant/common'
import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { SelectChangeEvent, Stack } from '@mui/material'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useConfigUI } from '../../context'
import { useGuidedEdit } from '@/features/config/contexts/guided-edit-context'
import EtReferenceTable, {
  type EtReferenceGridActions,
} from './EtReferenceTable'
import classes from './style.module.scss'

interface EtReferenceProps {
  /**
   * Scopes the list to a controller other than the globally-selected one
   * (SCRUM-976): the setup flows pass the controller they are working on.
   */
  interfaceId?: number | string
}

const EtReference = ({
  interfaceId: propInterfaceId,
}: EtReferenceProps = {}) => {
  const MotionDiv = motion.div
  const { t } = useTranslation()
  const { openModal, setModalView } = useConfigUI()
  const { interfaceId: authInterfaceId } = useAuthContext()
  const interfaceId = propInterfaceId ?? authInterfaceId
  const { isViewOnly } = usePermission()

  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [rowCount, setRowCount] = useState<number | null>(null)
  const [keyword, setKeyword] = useState('')

  // The grid owns its own data loading; the page drives bulk edit mode, the
  // drawer and the delete modal.
  const gridActions = useRef<EtReferenceGridActions | null>(null)
  const bulkEditByDefault = useGuidedEdit('etReference', () =>
    gridActions.current?.save(),
  )

  useEffect(() => {
    if (bulkEditByDefault) setBulkEditMode(true)
  }, [bulkEditByDefault])

  const handleRowCountChange = useCallback((count: number) => {
    setRowCount(count)
  }, [])

  // Search runs on the server (Keyword param), so it matches every reference.
  const handleSearch = useMemo(
    () =>
      debounce((event: SelectChangeEvent) => {
        setKeyword(event.target.value.toString().trim())
      }, 500),
    [],
  )

  const handleToggleBulkEdit = async () => {
    if (bulkEditMode) {
      const result = await gridActions.current?.save()
      // Stay in bulk edit when something failed, so the pending changes are not
      // lost and can be retried.
      if (result && result.failed > 0) return
    }
    setBulkEditMode(!bulkEditMode)
  }

  const handleCancelBulkEdit = () => {
    gridActions.current?.cancel()
    setBulkEditMode(false)
  }

  const handleDeleteItem = (id: string, name?: string) => {
    if (isViewOnly) return
    openModal()
    setModalView({
      type: 'DELETE_REFERENCE_VIEW',
      id: id.toString(),
      interfaceId: String(interfaceId),
      name: name,
      onSuccess: async () => {
        // The row is gone: a staged edit on it could never land.
        gridActions.current?.forgetRow(String(id))
        gridActions.current?.refresh()
      },
    })
  }

  return (
    <Stack className={classes.wrapper}>
      {/* Wraps rather than overlaps: in the four-column Station Setup layout
          these panels squeeze to ~200px, where the title and the bulk-edit
          buttons collided and "Save" was clipped to "S". */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap="8px"
        mb="12px"
        sx={{ minWidth: 0 }}
      >
        {/* SCRUM-1032: no search in the wizard. The step is twelve months of
            one short list being filled in, and a filtered grid there hides
            rows the user has not reached yet. */}
        {!bulkEditByDefault && (
          <SearchInput
            name={t('commonActionSearchETReference')}
            onChange={handleSearch}
          />
        )}

        <Stack direction="row" spacing={2} alignItems="center">
          {/* SCRUM-1032: the wizard's Next flushes every grid and the list
              writes itself on a pause, so its own Save and Cancel — and the
              Edit toggle, since bulk edit is always on there — are hidden. */}
          {!bulkEditByDefault && (bulkEditMode ? (
            <>
              <ButtonField
                startIcon={<CancelIcon />}
                style={{
                  height: '32px',
                  minWidth: '70px',
                  borderRadius: '5px',
                  backgroundColor: '#fff2f2',
                  color: '#dc3545',
                  border: '1px solid #f3c2c5',
                }}
                name={t('commonActionsCancel')}
                onClick={handleCancelBulkEdit}
              />
              <ButtonField
                startIcon={<SaveIcon />}
                style={{
                  height: '32px',
                  minWidth: '70px',
                  borderRadius: '5px',
                  backgroundColor: '#004744',
                  color: '#fff',
                }}
                name={t('commonActionsSave')}
                onClick={handleToggleBulkEdit}
              />
            </>
          ) : (
            <ButtonField
              startIcon={<EditIcon />}
              style={{
                height: '32px',
                minWidth: '89px',
                borderRadius: '5px',
                opacity: isViewOnly ? 0.6 : 1,
              }}
              name={t('commonActionsBulkEdit')}
              onClick={handleToggleBulkEdit}
              disabled={isViewOnly}
            />
          ))}
        </Stack>
      </Stack>

      <MotionDiv
        initial="initial"
        animate="animate"
        variants={flyInVariantsDashboard}
        className={classes.content}
      >
        {/* Keep the grid mounted while empty in bulk edit mode: the create row
            lives in it, so unmounting would remove the only way to add the
            first reference. */}
        {rowCount === 0 && !bulkEditMode ? (
          <NoData text={t('noData')} />
        ) : (
          <EtReferenceTable
            interfaceId={Number(interfaceId || 0)}
            keyword={keyword}
            bulkEditMode={bulkEditMode}
            onDeleteItem={handleDeleteItem}
            onRowCountChange={handleRowCountChange}
            actionsRef={gridActions}
          />
        )}
      </MotionDiv>
    </Stack>
  )
}

export default EtReference
